import { cva } from 'class-variance-authority';

import {
  TYPE_SCALE,
  COLOR_SCALE,
  INTERACTIVE_STATES,
  INTERACTIVE_HEIGHTS,
  SURFACE_PADDING,
} from '@/components/design-system/tokens';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'destructive'
  | 'danger'
  | 'outline'
  | 'ghost'
  | 'link'
  | 'success'
  | 'warning'
  | 'info'
  | 'pagination'
  | 'paginationActive'
  | 'elevated'
  | 'tertiary'
  | 'oauth'
  | 'ai'
  | 'default';

export type ButtonSize = 'sm' | 'md' | 'lg' | 'default' | 'icon';

export interface ButtonVariants {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

export const buttonVariants = cva(
  `inline-flex items-center justify-center gap-2 font-medium ${INTERACTIVE_STATES.transitions.all} ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98] hover:scale-[1.02] transition-transform duration-150`,
  {
    variants: {
      variant: {
        primary:
          'bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-sm focus-visible:ring-primary shadow-sm',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80 hover:shadow-sm focus-visible:ring-secondary shadow-sm',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90 hover:shadow-sm focus-visible:ring-destructive shadow-sm',
        danger:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90 hover:shadow-sm focus-visible:ring-destructive shadow-sm', // alias for destructive
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground hover:border-accent-foreground/20 hover:shadow-sm focus-visible:ring-ring shadow-sm',
        ghost:
          'hover:bg-accent hover:text-accent-foreground hover:shadow-sm focus-visible:ring-ring',
        link: 'text-primary hover:text-primary/80 focus-visible:ring-0 focus-visible:ring-offset-0',
        success: COLOR_SCALE['button-success'],
        warning: COLOR_SCALE['button-warning'],
        info: COLOR_SCALE['button-info'],
        pagination:
          'border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring',
        paginationActive:
          'bg-primary text-primary-foreground hover:bg-primary/90 focus-visible:ring-primary',
        elevated:
          'border border-border bg-background text-foreground shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200',
        tertiary: 'bg-muted text-muted-foreground hover:bg-muted/80 focus-visible:ring-ring',
        oauth:
          'border border-input bg-background text-foreground hover:border-border focus-visible:ring-ring',
        ai: COLOR_SCALE['button-ai'],
        default:
          'bg-primary text-primary-foreground hover:bg-primary/90 focus-visible:ring-primary', // alias for primary
      },
      size: {
        sm: `h-9 px-3 py-2 ${TYPE_SCALE.xs}`, // Increased from h-7 to h-9 for better touch targets
        md: `${INTERACTIVE_HEIGHTS.touchTarget} ${SURFACE_PADDING['4']} ${TYPE_SCALE.base}`, // Use touchTarget (44px) for standard buttons
        lg: `${INTERACTIVE_HEIGHTS.comfortable} ${SURFACE_PADDING['6']} ${TYPE_SCALE.lg}`,
        default: `${INTERACTIVE_HEIGHTS.touchTarget} ${SURFACE_PADDING['4']} ${TYPE_SCALE.base}`, // Use touchTarget for default
        icon: `${INTERACTIVE_HEIGHTS.touchTarget} ${SURFACE_PADDING['2']} aspect-square`, // Ensure icon buttons meet touch target requirements
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);
